import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Laptop, Palette, Wrench, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';

/**
 * Absicherungs-Beispiele (§34d-konform)
 * Fokus auf BU-/Absicherungsfälle, Vorher/Nachher der Absicherungslücke
 * KEINE Kapitalmarkt-, Rendite- oder Immobilien-Prognosen!
 */

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)} Mio. €`;
  }
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const caseStudies = [
  {
    id: 1,
    icon: Laptop,
    title: 'IT-Consultant, 34 Jahre',
    subtitle: 'Selbstständig ohne Absicherung',
    situation: 'Senior Developer mit eigenem Unternehmen, 6.500 € netto monatlich. Keine BU-Versicherung, kein Krankentagegeld. "Mir passiert schon nichts."',
    problem: 'Als Selbstständiger besteht kein automatischer Anspruch auf Krankengeld. Nur bei normalem (nicht ermäßigtem) Beitragssatz oder mit Wahltarif zahlt die GKV, und das erst ab der 7. Woche (43. Tag). In den ersten 6 Wochen: 0 €. Bei Fixkosten von 4.200 € (Miete, Leasing, Versicherungen) entsteht sofort eine existenzbedrohende Lücke.',
    solution: 'BU-Versicherung mit 4.000 €/Monat abgeschlossen, dazu Krankentagegeld ab Tag 1 für die Zeit ohne gesetzlichen Anspruch. Monatlicher Beitrag: ca. 180 €. Die Absicherungslücke ist geschlossen.',
    before: {
      items: [
        { label: 'Nettoeinkommen', value: '6.500 €/Monat' },
        { label: 'Absicherung bei BU', value: '0 €', negative: true },
        { label: 'Erste 6 Wochen', value: '0 €', negative: true },
        { label: 'Fixkosten', value: '4.200 €' },
        { label: 'Sofortige Lücke', value: '-4.200 €/Monat', negative: true },
      ]
    },
    after: {
      items: [
        { label: 'Nettoeinkommen', value: '6.500 €/Monat' },
        { label: 'BU-Rente (versichert)', value: '4.000 €', positive: true },
        { label: 'Krankentagegeld (ab Tag 1)', value: '2.500 €', positive: true },
        { label: 'Fixkosten', value: '4.200 €' },
        { label: 'Absicherungslücke', value: '0 €', positive: true },
      ]
    },
    keyPoints: [
      { label: 'Vorher Lücke', value: '4.200 €/Monat', type: 'danger' },
      { label: 'Nachher Lücke', value: '0 €', type: 'success' },
      { label: 'Beitrag BU + KTG', value: '~180 €/Monat', type: 'neutral' },
    ]
  },
  {
    id: 2,
    icon: Palette,
    title: 'UX-Designerin, 29 Jahre',
    subtitle: 'Risikolebensversicherung vergessen',
    situation: 'Festangestellt bei einer Agentur, 3.800 € netto. Verheiratet, ein Kind. Der Partner arbeitet in Teilzeit. Keine Risikolebensversicherung abgeschlossen.',
    problem: 'Bei einem Todesfall müsste der Partner allein für die Familie sorgen, mit nur 1.800 € Teilzeiteinkommen. Die Miete allein beträgt 1.400 €. Die Witwen- und Waisenrente reicht bei weitem nicht.',
    solution: 'Risikolebensversicherung über 300.000 € abgeschlossen. Bei einem Todesfall kann der Partner mehrere Jahre überbrücken, sich neu orientieren oder die Arbeitszeit anpassen. Monatlicher Beitrag: ca. 18 €.',
    before: {
      items: [
        { label: 'Familieneinkommen', value: '5.600 €/Monat' },
        { label: 'Risikoleben', value: 'Nicht vorhanden', negative: true },
        { label: 'Im Todesfall verfügbar', value: '~1.800 €' },
        { label: 'Fixkosten Familie', value: '3.200 €' },
        { label: 'Unterdeckung', value: '-1.400 €/Monat', negative: true },
      ]
    },
    after: {
      items: [
        { label: 'Familieneinkommen', value: '5.600 €/Monat' },
        { label: 'Risikoleben', value: '300.000 €', positive: true },
        { label: 'Überbrückung möglich', value: '~8 Jahre', positive: true },
        { label: 'Fixkosten Familie', value: '3.200 €' },
        { label: 'Familie abgesichert', value: 'Ja', positive: true },
      ]
    },
    keyPoints: [
      { label: 'Vorher Absicherung', value: '0 €', type: 'danger' },
      { label: 'Nachher Absicherung', value: '300.000 €', type: 'success' },
      { label: 'Monatlicher Beitrag', value: '~18 €', type: 'neutral' },
    ]
  },
  {
    id: 3,
    icon: Wrench,
    title: 'Ingenieur, 38 Jahre',
    subtitle: 'BU zu niedrig, Krankentagegeld fehlt',
    situation: 'Angestellt im Maschinenbau, 5.200 € netto. Hat eine BU-Versicherung, aber nur über 1.500 €/Monat. Abgeschlossen mit 25, als das Gehalt noch niedriger war. Kein privates Krankentagegeld.',
    problem: 'Die BU-Rente deckt nicht mal ein Drittel des aktuellen Einkommens. Bei Berufsunfähigkeit würden 3.700 €/Monat fehlen. Kritisch wird es ab Woche 7: Dann zahlt die Krankenkasse nur noch Krankengeld (ca. 70 % des Bruttogehalts, gedeckelt) statt des vollen Gehalts. Ohne Krankentagegeld entsteht genau in dieser Phase eine spürbare Lücke.',
    solution: 'BU-Rente auf 3.500 €/Monat aufgestockt (Nachversicherungsgarantie genutzt). Zusätzlich Krankentagegeld für die Lücke zwischen Gehalt und Krankengeld eingerichtet.',
    before: {
      items: [
        { label: 'Nettoeinkommen', value: '5.200 €/Monat' },
        { label: 'BU-Rente (alt)', value: '1.500 €', negative: true },
        { label: 'Krankentagegeld', value: 'Nicht vorhanden', negative: true },
        { label: 'Fixkosten', value: '3.400 €' },
        { label: 'Lücke bei BU', value: '-1.900 €/Monat', negative: true },
      ]
    },
    after: {
      items: [
        { label: 'Nettoeinkommen', value: '5.200 €/Monat' },
        { label: 'BU-Rente (neu)', value: '3.500 €', positive: true },
        { label: 'Krankentagegeld', value: '1.700 €', positive: true },
        { label: 'Fixkosten', value: '3.400 €' },
        { label: 'Lücke geschlossen', value: '0 €', positive: true },
      ]
    },
    keyPoints: [
      { label: 'Vorher BU-Rente', value: '1.500 €', type: 'danger' },
      { label: 'Nachher BU-Rente', value: '3.500 €', type: 'success' },
      { label: 'Aufstockung Beitrag', value: '+85 €/Monat', type: 'neutral' },
    ]
  }
];

const CaseCard = ({ caseStudy }) => {
  const Icon = caseStudy.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl border border-black/5 shadow-lg overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-black/5 bg-gradient-to-r from-bone to-white">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cobalt/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-cobalt" />
          </div>
          <div>
            <h3 className="font-outfit text-base sm:text-lg md:text-xl font-bold text-navy">{caseStudy.title}</h3>
            <p className="text-xs sm:text-sm text-slate">{caseStudy.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2">
        {/* Left: Story */}
        <div className="p-4 sm:p-6 lg:border-r border-black/5">
          <div className="space-y-3 sm:space-y-4">
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate mb-1">Ausgangssituation</div>
              <p className="text-charcoal text-xs sm:text-sm leading-relaxed">{caseStudy.situation}</p>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-red-500 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Das Risiko
              </div>
              <p className="text-charcoal text-xs sm:text-sm leading-relaxed">{caseStudy.problem}</p>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald mb-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Die Lösung
              </div>
              <p className="text-charcoal text-xs sm:text-sm leading-relaxed">{caseStudy.solution}</p>
            </div>
          </div>

          {/* Key Points - Mobile optimiert */}
          <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mt-4 sm:mt-6">
            {caseStudy.keyPoints.map((point, i) => (
              <div 
                key={i} 
                className={`p-2 sm:p-3 rounded-lg text-center ${
                  point.type === 'danger' ? 'bg-red-50' :
                  point.type === 'success' ? 'bg-emerald/10' : 'bg-bone'
                }`}
              >
                <div className={`font-mono text-xs sm:text-base font-bold whitespace-nowrap ${
                  point.type === 'danger' ? 'text-red-500' :
                  point.type === 'success' ? 'text-emerald' : 'text-navy'
                }`}>
                  {point.value}
                </div>
                <div className="text-[8px] sm:text-xs text-slate leading-tight mt-0.5">{point.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Vorher/Nachher Vergleich - Mobile Stack */}
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Vorher */}
            <div className="bg-red-50/50 rounded-xl p-3 sm:p-4 border border-red-200/50">
              <div className="text-xs sm:text-sm font-semibold text-red-800 mb-2 sm:mb-3 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Vorher
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                {caseStudy.before.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center gap-2">
                    <span className="text-[11px] sm:text-xs text-charcoal">{item.label}</span>
                    <span className={`font-mono text-[11px] sm:text-xs font-medium whitespace-nowrap ${item.negative ? 'text-red-600' : 'text-charcoal'}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Nachher */}
            <div className="bg-emerald-50/50 rounded-xl p-3 sm:p-4 border border-emerald-200/50">
              <div className="text-xs sm:text-sm font-semibold text-emerald-800 mb-2 sm:mb-3 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                Nachher
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                {caseStudy.after.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center gap-2">
                    <span className="text-[11px] sm:text-xs text-charcoal">{item.label}</span>
                    <span className={`font-mono text-[11px] sm:text-xs font-medium whitespace-nowrap ${
                      item.positive ? 'text-emerald-600' : 
                      item.negative ? 'text-red-600' : 'text-charcoal'
                    }`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const AbsicherungsBeispiele = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? caseStudies.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === caseStudies.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-white relative" data-testid="absicherungs-beispiele">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="text-center mb-8 md:mb-12">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3 md:mb-4">Aus der Praxis</div>
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy tracking-tight mb-3 md:mb-4 px-2">
            Absicherungslücke? So schnell ist sie geschlossen.
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-charcoal max-w-2xl mx-auto px-2">
            Echte Situationen, anonymisiert. So haben andere ihre Absicherung strukturiert.
          </p>
        </div>

        {/* Case Carousel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <CaseCard 
              key={caseStudies[activeIndex].id}
              caseStudy={caseStudies[activeIndex]} 
            />
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-bone border border-black/5 flex items-center justify-center hover:bg-white hover:border-cobalt/20 transition-all"
              data-testid="case-prev-btn"
              aria-label="Vorheriges Beispiel"
            >
              <ChevronLeft className="w-5 h-5 text-navy" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {caseStudies.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === activeIndex ? 'bg-cobalt w-8' : 'bg-slate/30 hover:bg-slate/50 w-2.5'
                  }`}
                  data-testid={`case-dot-${i}`}
                  aria-label={`Beispiel ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-bone border border-black/5 flex items-center justify-center hover:bg-white hover:border-cobalt/20 transition-all"
              data-testid="case-next-btn"
              aria-label="Nächstes Beispiel"
            >
              <ChevronRight className="w-5 h-5 text-navy" />
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-[10px] sm:text-xs text-slate text-center mt-6 sm:mt-8 max-w-2xl mx-auto px-2">
          Alle Fallbeispiele sind anonymisiert und basieren auf realen Kundensituationen. 
          Die genannten Beiträge sind Richtwerte. Die tatsächlichen Kosten hängen von Beruf, Gesundheit und Anbieter ab.
        </p>
      </div>
    </section>
  );
};

export default AbsicherungsBeispiele;
